// Centralized react-query hooks. Every queryKey lives here exactly once,
// bound to its queryFn. This eliminates the entire "two callers share a
// queryKey but return different shapes" bug class (see 2026-05-18 incident:
// d.filter is not a function caused by mixed PageResponse vs items-array
// caching under ["assets"]).
//
// Rules:
//   1. New endpoints add a hook here, NOT a useQuery in a view.
//   2. Paged endpoints (PageResponse) call `apiFetchPaged` from lib/api so the
//      envelope shape is validated at the boundary.
//   3. Hooks expose `items` as a convenience array for paged data, always a
//      real Array (never undefined), so consumers can do
//      `const { items: assets } = useAssetsQuery()` and skip the `?? []` dance.

import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { apiFetch, apiFetchPaged } from "./api";
import type {
  Asset,
  GenerationModel,
  JobDetail,
  JobListItem,
  PageResponse,
  StylePreset,
  Template,
} from "./types";

const FREE_CREATION_MODEL_CODES = [
  "gpt-image-2",
  "nano-banana",
  "wan2.7-image",
  "doubao-seedream-4-5-251128",
  "doubao-seedream-5.0-lite",
  "qwen-image-2.0",
] as const;

// ── Shared option shape for paged hooks ────────────────────────────────
type PagedOptions<T> = Omit<
  UseQueryOptions<PageResponse<T>, Error>,
  "queryKey" | "queryFn"
>;

function withItems<T>(query: ReturnType<typeof useQuery<PageResponse<T>, Error>>) {
  // Belt-and-suspenders: apiFetchPaged already validates `items` is an array,
  // but if any caller wires this hook to a non-paged source we still hand back
  // a guaranteed-array.
  const items = Array.isArray(query.data?.items) ? (query.data!.items as T[]) : [];
  return Object.assign(query, { items });
}

// ── Singletons / lookups ──────────────────────────────────────────────

export function useMeQuery(
  options?: Omit<UseQueryOptions<unknown, Error>, "queryKey" | "queryFn">,
) {
  return useQuery<unknown, Error>({
    queryKey: ["me"],
    queryFn: () => apiFetch<unknown>("/api/v2/users/me"),
    refetchInterval: 30_000,
    ...options,
  });
}

export function useModelsQuery() {
  return useQuery({
    queryKey: ["models"],
    queryFn: async () => {
      const data = await apiFetchPaged<GenerationModel>("/api/v2/generation-models");
      return data.items
        .filter((m) => FREE_CREATION_MODEL_CODES.includes(m.code as typeof FREE_CREATION_MODEL_CODES[number]))
        .sort(
          (a, b) =>
            FREE_CREATION_MODEL_CODES.indexOf(a.code as typeof FREE_CREATION_MODEL_CODES[number]) -
            FREE_CREATION_MODEL_CODES.indexOf(b.code as typeof FREE_CREATION_MODEL_CODES[number]),
        );
    },
    staleTime: Infinity,
  });
}

// ── Paged endpoints ───────────────────────────────────────────────────

export function useAssetsQuery(options?: PagedOptions<Asset>) {
  return withItems(
    useQuery<PageResponse<Asset>, Error>({
      queryKey: ["assets"],
      queryFn: () => apiFetchPaged<Asset>("/api/v2/assets?limit=100"),
      ...options,
    }),
  );
}

// Specialized: only generated images. Different queryKey so it doesn't collide
// with the general ["assets"] cache.
export function useGeneratedAssetsQuery(options?: PagedOptions<Asset>) {
  return withItems(
    useQuery<PageResponse<Asset>, Error>({
      queryKey: ["assets", "generated"],
      queryFn: () =>
        apiFetchPaged<Asset>("/api/v2/assets?asset_type=generated_image&limit=200"),
      ...options,
    }),
  );
}

export function useTemplatesQuery(options?: PagedOptions<Template>) {
  return withItems(
    useQuery<PageResponse<Template>, Error>({
      queryKey: ["templates"],
      queryFn: () => apiFetchPaged<Template>("/api/v2/templates?limit=100"),
      staleTime: Infinity,
      ...options,
    }),
  );
}

export function useStylePresetsQuery(options?: PagedOptions<StylePreset>) {
  return withItems(
    useQuery<PageResponse<StylePreset>, Error>({
      queryKey: ["style-presets"],
      queryFn: () => apiFetchPaged<StylePreset>("/api/v2/style-presets"),
      staleTime: Infinity,
      ...options,
    }),
  );
}

export function useJobsQuery(options?: PagedOptions<JobListItem>) {
  return withItems(
    useQuery<PageResponse<JobListItem>, Error>({
      queryKey: ["jobs"],
      queryFn: () =>
        apiFetchPaged<JobListItem>("/api/v2/generation-jobs?limit=100"),
      ...options,
    }),
  );
}

// ── Billing ───────────────────────────────────────────────────────────

export interface BalanceInfo {
  credit_balance: number;
  daily_limit: number | null;
  daily_consumed: number;
  daily_remaining: number | null;
  lifetime_consumed: number;
}

export interface Transaction {
  transaction_id: string;
  transaction_type: string;
  amount: number;
  balance_after: number;
  related_job_id: string | null;
  remark: string | null;
  created_at: string;
}

export interface TransactionFilters {
  action?: "topup" | "consume" | "refund" | "";
  from?: string;
  to?: string;
}

export function useBalanceQuery() {
  return useQuery<BalanceInfo, Error>({
    queryKey: ["billing", "balance"],
    queryFn: () => apiFetch<BalanceInfo>("/api/v2/billing/balance"),
  });
}

export function useTransactionsQuery(filters: TransactionFilters = {}) {
  return withItems(
    useQuery<PageResponse<Transaction>, Error>({
      queryKey: ["transactions", filters],
      queryFn: () => {
        const params = new URLSearchParams({ limit: "100" });
        if (filters.action) params.set("action", filters.action);
        if (filters.from) params.set("from", filters.from);
        if (filters.to) params.set("to", filters.to);
        return apiFetchPaged<Transaction>(
          `/api/v2/billing/transactions?${params.toString()}`,
        );
      },
    }),
  );
}

// ── Job detail ────────────────────────────────────────────────────────

export function useJobDetailQuery(
  jobId: string | null,
  options?: Omit<UseQueryOptions<JobDetail, Error>, "queryKey" | "queryFn" | "enabled">,
) {
  return useQuery<JobDetail, Error>({
    queryKey: ["job-detail", jobId],
    queryFn: () => apiFetch<JobDetail>(`/api/v2/generation-jobs/${jobId}`),
    enabled: !!jobId,
    ...options,
  });
}
